import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { useCatalog } from "../../catalog/CatalogContext";
import ShareButtons from "../../components/Buttons/ShareButtons";
import OrderModal from "../../components/OrderModal/OrderModal";
import { buildProductSlug, Product } from "../../data/products";

export default function ShoesCollection() {
  const { products } = useCatalog();
  const [index, setIndex] = useState(0);
  const [visibleCards, setVisibleCards] = useState(4);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const shoes = products.filter((product) => product.category === "shoes");

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) setVisibleCards(1.1);
      else if (window.innerWidth < 1024) setVisibleCards(2.15);
      else setVisibleCards(4);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const maxIndex = Math.max(shoes.length - Math.floor(visibleCards), 0);
  const next = () => setIndex((prev) => (prev < maxIndex ? prev + 1 : prev));
  const prev = () => setIndex((prev) => (prev > 0 ? prev - 1 : prev));
  const isMobile = visibleCards < 2;

  useEffect(() => {
    if (index > maxIndex) {
      setIndex(maxIndex);
    }
  }, [index, maxIndex]);

  if (!shoes.length) return null;

  return (
    <>
      <section className="section-divider px-4 py-14 md:px-8 lg:px-10 lg:py-18">
        <div className="mx-auto max-w-7xl">
          <div className="mb-10 flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="brand-eyebrow">Chaussures</p>
              <h2 className="brand-display mt-3 text-4xl font-bold text-[#1f1410] md:text-6xl">
                Des chaussures pour faire sensation.
              </h2>
            </div>
            <div className="max-w-md">
              <p className="text-sm leading-7 text-[#715c50]">
                Talons, sandales et modeles tendance choisis pour apporter du
                style et de l’elegance a vos sorties.
              </p>
              <Link
                to="/products/shoes"
                className="mt-4 inline-flex items-center text-xs font-semibold uppercase tracking-[0.3em] text-[#7a3f24]"
              >
                Voir toute la collection
              </Link>
            </div>
          </div>

          <div className="surface-panel overflow-hidden rounded-[34px] p-4 md:p-6">
            <div className="overflow-x-auto md:overflow-hidden">
              <div
                className="flex gap-5 transition-transform duration-700 ease-out"
                style={{
                  transform: isMobile
                    ? "none"
                    : `translateX(-${index * (100 / visibleCards)}%)`,
                }}
              >
                {shoes.map((shoe) => (
                  <article
                    key={shoe.id}
                    className="surface-card min-w-[88%] rounded-[30px] p-4 sm:min-w-[46%] lg:min-w-[calc(25%-0.95rem)]"
                  >
                    <Link
                      to={`/product/${buildProductSlug(shoe)}`}
                      className="group block rounded-[24px] bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.9),rgba(238,221,205,0.9))] p-6"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <span className="rounded-full bg-white/80 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.24em] text-[#8b634d]">
                          Chaussures
                        </span>
                        <span className="text-sm font-semibold text-[#2b1b15]">
                          {shoe.price}
                        </span>
                      </div>

                      <div className="flex min-h-[250px] items-center justify-center py-6">
                        <img
                          src={shoe.image}
                          alt={shoe.title}
                          className="max-h-[220px] max-w-full object-contain transition duration-700 group-hover:scale-105 group-hover:-rotate-2"
                        />
                      </div>
                    </Link>

                    <div className="mt-5">
                      <h3 className="brand-display text-2xl font-bold text-[#221510]">
                        {shoe.title}
                      </h3>
                      <p className="mt-2 text-sm leading-7 text-[#735d51]">
                        {shoe.description ??
                          "Une paire ideale pour completer votre allure avec style."}
                      </p>
                    </div>

                    <div className="mt-6 flex items-center justify-between gap-3">
                      <button
                        type="button"
                        onClick={() => setSelectedProduct(shoe)}
                        className="cta-primary rounded-full px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.26em] transition"
                      >
                        Commander
                      </button>
                      <ShareButtons
                        title={shoe.title}
                        price={shoe.price}
                        slug={buildProductSlug(shoe)}
                      />
                    </div>
                  </article>
                ))}
              </div>
            </div>

            <div className="mt-8 hidden justify-center gap-3 md:flex">
              <button
                type="button"
                onClick={prev}
                disabled={index === 0}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-[rgba(62,35,24,0.12)] bg-white/80 text-lg text-[#2b1b15] disabled:opacity-35"
              >
                ‹
              </button>
              <button
                type="button"
                onClick={next}
                disabled={index === maxIndex}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-[rgba(62,35,24,0.12)] bg-white/80 text-lg text-[#2b1b15] disabled:opacity-35"
              >
                ›
              </button>
            </div>
          </div>
        </div>
      </section>

      <OrderModal
        product={selectedProduct}
        onClose={() => setSelectedProduct(null)}
      />
    </>
  );
}
